import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Flex, Icon, Text, Button, EditableText, iconWeight } from '@nula/core-ui';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { useUIStore, type ActiveSurface } from '@/stores/use-ui-store';
import { surfaceMeta } from '@/lib/sync/surface-meta';
import { renameSurface } from '@/lib/sync/rename-surface';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import { EntityProperties } from '@/components/entity-properties/entity-properties';
import styles from './entity-title-header.module.css';

export interface EntityTitleHeaderProps {
  entityId: string;
  showTitle: boolean;
}

/**
 * Editable title bar shared by every entity view (documents, canvases, …).
 * Entity-agnostic: reads/writes the workspace doc via lib hooks off a generic
 * entityId, so any feature can mount it above its body without cross-feature
 * coupling.
 */
export function EntityTitleHeader({ entityId, showTitle }: EntityTitleHeaderProps) {
  const workspaceDoc = useWorkspaceDoc(entityId);
  const service = useWorkspaceService();
  const canEdit = useCanEdit(entityId);
  const [titleEditing, setTitleEditing] = useState(() => !workspaceDoc?.title);
  const userInitiatedEdit = useRef(false);

  const activeSurface = useUIStore((s) => s.getActiveSurface(entityId));
  const requestSurface = useUIStore((s) => s.requestSurface);
  // Renaming the active surface writes the manifest, so the header needs the
  // Yjs doc. Room subscriptions are refcounted, so this shares the editor's.
  const { doc } = useEntityYjs(entityId, 'document');

  useEffect(() => {
    if (workspaceDoc?.title && titleEditing && !userInitiatedEdit.current) {
      setTitleEditing(false);
    }
  }, [workspaceDoc?.title, titleEditing]);

  const setTitleEditingWrapped = (editing: boolean) => {
    userInitiatedEdit.current = editing;
    setTitleEditing(editing);
  };

  if (!showTitle) return null;

  // The chips describe a MEETING, and a meeting is a surface — its attendees,
  // its calendar event. At the document root there is no active surface and so
  // no meeting to describe: a document is a container for several unrelated
  // meetings, none of which owns the container's title bar. A resolved surface
  // is itself the context, whether or not its event has been linked yet.
  //
  // The surface takes the heading and the document steps back to a crumb. An
  // id whose entry never arrived (the store keeps its entry after SurfaceMount
  // unmounts) is not a surface we can name or describe, so it falls through to
  // the document title alone — chips included.
  if (activeSurface) {
    return (
      <Flex vertical className={styles.headerBar} data-companion-surface="perch">
        <Flex align="center" gap="xs" className={styles.headerContent}>
          <SurfaceBreadcrumb
            documentTitle={workspaceDoc?.title ?? 'Untitled'}
            surface={activeSurface}
            canEdit={canEdit}
            onNavigateToDocument={() => requestSurface(entityId, null)}
            onRename={(title) => renameSurface(doc, activeSurface.id, title)}
          />
        </Flex>
        <EntityProperties entityId={entityId} surface={activeSurface} />
      </Flex>
    );
  }

  return (
    <Flex vertical className={styles.headerBar} data-companion-surface="perch">
      <Flex align="center" className={styles.headerContent}>
        <EditableText
          value={workspaceDoc?.title ?? ''}
          onChange={(val) => {
            const trimmed = val.trim();
            if (trimmed !== (workspaceDoc?.title ?? '')) {
              service.renameDoc(entityId, trimmed || 'Untitled');
            }
            setTitleEditingWrapped(false);
          }}
          onCancel={() => setTitleEditingWrapped(false)}
          editing={titleEditing && canEdit}
          onEditingChange={(editing) => {
            if (canEdit) setTitleEditingWrapped(editing);
          }}
          emptyText="Untitled"
          size="lg"
          className={styles.titleEditableText}
          renderDisplay={(value) => (
            <Flex align="center" gap="sm" className={styles.titleDisplayRow}>
              <Text className={clsx(styles.titleDisplay, !value && styles.titleDisplayUntitled)}>
                {value || 'Untitled'}
              </Text>
              {canEdit && (
                <Icon
                  name="edit"
                  size={14}
                  weight={iconWeight.light}
                  className={styles.titleEditIcon}
                />
              )}
            </Flex>
          )}
        />
      </Flex>
      {/* The document's own properties — tags, collections, last edited. The
          surface-scoped rows are absent here by capability, not by omission:
          a container holds meetings, it does not have one. */}
      <EntityProperties entityId={entityId} surface={null} />
    </Flex>
  );
}

interface SurfaceBreadcrumbProps {
  documentTitle: string;
  surface: ActiveSurface;
  canEdit: boolean;
  onNavigateToDocument: () => void;
  onRename: (title: string) => void;
}

/**
 * "document › surface". The parent returns to the document body; the leaf
 * names what is on screen and carries its kind glyph.
 */
function SurfaceBreadcrumb({
  documentTitle,
  surface,
  canEdit,
  onNavigateToDocument,
  onRename,
}: SurfaceBreadcrumbProps) {
  const meta = surfaceMeta(surface);

  return (
    <Flex align="center" gap="xs" className={styles.crumb}>
      <Button
        variant="text"
        size="sm"
        onClick={onNavigateToDocument}
        title={documentTitle}
        className={styles.crumbParent}
        data-testid="crumb-parent"
      >
        {documentTitle}
      </Button>
      <Icon
        name="chevron_right"
        size={14}
        weight={iconWeight.light}
        aria-hidden="true"
        className={styles.crumbSeparator}
      />
      <Flex align="center" gap="xs" className={styles.crumbLeaf} data-testid="crumb-leaf">
        <Icon name={meta.icon} size={16} weight={iconWeight.light} className={styles.crumbIcon} />
        {canEdit ? (
          <EditableText
            value={surface.title}
            onChange={onRename}
            size="lg"
            className={styles.crumbLeafText}
            data-testid="surface-title"
            renderDisplay={(value) => (
              <Flex align="center" gap="sm" className={styles.titleDisplayRow}>
                <Text className={styles.titleDisplay} title={value}>
                  {value}
                </Text>
                <Icon
                  name="edit"
                  size={14}
                  weight={iconWeight.light}
                  className={styles.titleEditIcon}
                />
              </Flex>
            )}
          />
        ) : (
          <Text className={styles.titleDisplay} title={surface.title}>
            {surface.title}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
